// PrivateProfile.ts
// User's private profile model - migrated from iOS app
// Contains user progress, preferences, and activity data

import { z } from 'zod';

// === NEW ACTIVE SCHEMAS ===

export const UserBookProgressSchema = z.object({
  id: z.string(), // Book ID
  startedAt: z.number(), // Unix timestamp when user first accessed
  lastAccessedAt: z.number(), // Unix timestamp of last access
  completionPercentage: z.number().min(0).max(100), // 0-100 percentage of book completed
  isFavorite: z.boolean(), // Whether user marked as favorite
  favoriteAddedAt: z.number().optional(), // When added to favorites
  totalTimeSpent: z.number(), // Total seconds spent reading/engaging
  currentInsightId: z.string().optional(), // Current insight/chapter user is on
  notesCount: z.number(), // Number of notes/highlights user made
  isCompleted: z.boolean(), // Whether user finished the book
  completedAt: z.number().optional(), // When book was completed
  recommendationReason: z.string().optional(), // AI's reason for recommending this book
  recommendedAt: z.number().optional(), // When this book was recommended
});

export const UserInsightProgressSchema = z.object({
  id: z.string(), // Insight ID
  bookId: z.string(), // Parent book ID
  startedAt: z.number(), // Unix timestamp when user first accessed
  lastAccessedAt: z.number(), // Unix timestamp of last access
  isCompleted: z.boolean(), // Whether user completed this insight
  completedAt: z.number().optional(), // When insight was completed
  isSaved: z.boolean().optional(), // Whether user saved/bookmarked this insight
  savedAt: z.number().optional(), // When insight was saved
  recommendationReason: z.string().optional(), // AI's reason for recommending this insight
  recommendedAt: z.number().optional(), // When this insight was recommended
  lastShownAsInsightOfDayDate: z.string().optional(), // Date when last shown as "Insight of the Day" (YYYY-MM-DD)
});

export const OnboardingAnswerSchema = z.object({
  questionText: z.string(), // The actual question asked
  answer: z.string(), // The user's answer
});

export const OnboardingAnswersCollectionSchema = z.object({
  sessionId: z.string(), // Unique ID for this onboarding session
  completedAt: z.number(), // Unix timestamp when onboarding was completed
  version: z.string(), // Version of onboarding flow (for tracking changes)
  answers: z.array(OnboardingAnswerSchema), // All answers from the session
});

export const ActiveContextSchema = z.object({
  lastUpdated: z.number(), // Timestamp of last update
  source: z.enum(['onboarding', 'profile_update', 'daily_query', 'conversation', 'creation', 'external_import', 'ai_extraction', 'abbreviated_onboarding']), // Where update came from
  recentInsights: z.array(z.string()).optional(),
  currentFocus: z.array(z.string()).optional(),
  emotionalState: z.string().optional(),
  urgentNeeds: z.array(z.string()).optional(),
  information: z.string().optional(), // For general notes or raw text
});

export const CoreProfileSchema = z.object({
  goals: z.array(z.string()).optional(),
  interests: z.array(z.string()).optional(),
  challenges: z.array(z.string()).optional(),
  personalityTraits: z.array(z.string()).optional(),
  mainFocusCategory: z.enum([
    'health_wellness',
    'career_professional',
    'leadership_management',
    'entrepreneurship_business',
    'relationships_family',
    'personal_development',
    'finance_wealth',
    'learning_skills',
    'productivity_time',
    'purpose_meaning'
  ]).optional(),
  mainFocusLabel: z.string().optional(),
  primaryGoalText: z.string().optional(),
  lastUpdated: z.number(),
  source: z.string(),
});

// Device info schema - saved once to profile, not sent with every log
export const DeviceInfoSchema = z.object({
  // Platform basics
  platform: z.string(), // "iOS" | "Android" | "Web"
  osName: z.string().optional(),
  osVersion: z.string().optional(),
  deviceInfo: z.string().optional(), // Formatted string like "iOS v17.2"
  
  // Device details (native only)
  deviceName: z.string().optional(),
  deviceModel: z.string().optional(),
  deviceBrand: z.string().optional(),
  deviceManufacturer: z.string().optional(),
  deviceYearClass: z.union([z.string(), z.number()]).optional(),
  deviceType: z.string().optional(), // "Phone" | "Tablet" | "Desktop" | "TV"
  isDevice: z.boolean().optional(), // true for physical device, false for emulator
  
  // Screen info
  screenWidth: z.number().optional(),
  screenHeight: z.number().optional(),
  pixelRatio: z.number().optional(),
  
  // Location
  countryCode: z.string().optional(),
  
  // Tracking IDs
  storeId: z.string().optional(),
  appsFlyerId: z.string().optional(),
  
  // Metadata
  lastUpdated: z.number(), // Unix timestamp when this was last updated
  appVersionWhenCaptured: z.string().optional(), // App version when device info was captured
});

// Flexible calculation results - accepts any JSON structure
// Different onboardings may have completely different calculation formats
export const CalculationResultsSchema = z.record(z.any());

// === NEW AI RECOMMENDATION SCHEMAS ===

export const AIBookRecommendationSchema = z.object({
  bookId: z.string(), // Firestore book ID
  reason: z.string(), // Personalized reason for recommendation
  recommendedAt: z.number(), // Unix timestamp
});

export const AIInsightRecommendationSchema = z.object({
  insightId: z.string(), // Firestore insight ID
  reason: z.string(), // Personalized reason for recommendation
  bookId: z.string().optional(), // Parent book ID if known
  recommendedAt: z.number(), // Unix timestamp
});

export const DailyRecommendationsSchema = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  personalizedMessage: z.string(), // AI's personalized message
  recommendedBooks: z.array(AIBookRecommendationSchema),
  insightPlan: z.object({
    title: z.string(),
    insights: z.array(AIInsightRecommendationSchema),
  }).optional(),
  query: z.string().optional(), // User's query that triggered recommendations
  generatedAt: z.number(), // Unix timestamp
});

export const ProfileSummarySchema = z.object({
  shortSummary: z.array(z.object({
    label: z.string(),
    value: z.string(),
  })).optional(),
  fullDescription: z.array(z.object({
    title: z.string(),
    description: z.string(),
  })).optional(),
  lastUpdated: z.number(), // Unix timestamp
});

export const MentorChatHistorySchema = z.object({
  id: z.string(), // Unique chat ID
  timestamp: z.number(), // Unix timestamp
  userMessage: z.string(),
  assistantResponse: z.string(),
  recommendations: z.object({
    books: z.array(AIBookRecommendationSchema).optional(),
    insights: z.array(AIInsightRecommendationSchema).optional(),
  }).optional(),
  profileUpdates: z.object({
    coreProfile: z.object({
      goals: z.array(z.string()).optional(),
      challenges: z.array(z.string()).optional(),
      interests: z.array(z.string()).optional(),
      values: z.array(z.string()).optional(),
      personalityTraits: z.array(z.string()).optional(),
    }).optional(),
    activeContext: z.object({
      currentFocus: z.array(z.string()).optional(),
      urgentNeeds: z.array(z.string()).optional(),
      emotionalState: z.string().optional(),
      information: z.string().optional(),
    }).optional(),
    changesSummary: z.string().optional(),
  }).optional(),
});

// === MAIN PROFILE SCHEMA ===

export const PrivateProfileSchema = z.object({
  // === CORE USER IDENTITY ===
  id: z.string(), // Firebase UID
  email: z.string().email().optional(),
  displayName: z.string().optional(), // User's display name
  uuid: z.string().nullable().optional(), // Device UUID - may not be needed on RN
  
  // === TIMESTAMPS ===
  createdAtTimestamp: z.number(), // Unix timestamp
  updatedAtTimestamp: z.number(), // Unix timestamp
  
  // === ACTIVITY ===
  dailyActivity: z.array(z.number()), // Array of daily activity timestamps
  
  // === APP METADATA ===
  platform: z.string(), // "React Native" | "iOS" | "Android"
  appVersion: z.string().optional(), // Current app version
  startingAppVersion: z.string().optional(), // Version when user first installed
  timezone: z.string().optional(), // User's timezone
  countryCode: z.string().nullable().optional(), // User's country

  // === AUDIENCE SEGMENTS ===
  // Explicit audiences for filtering recommendations; empty means general/unknown.
  audiences: z.array(z.string()).optional(),
  
  // === SUBSCRIPTION & USER STATUS ===
  // RevenueCat subscription (source of truth)
  subscriptionTier: z.enum(['free', 'standard_premium', 'ai_premium']), // Current subscription tier
  subscriptionExpiresAt: z.number().nullable().optional(), // Unix timestamp when subscription expires
  lastRevenueCatSync: z.number().optional(), // When we last synced with RevenueCat
  revenueCatCustomerId: z.string().optional(), // RevenueCat customer ID for debugging
  
  isTester: z.boolean(), // Beta tester flag
  isEmailVerified: z.boolean().optional(), // Email verification status
  
  // === NEW CONTENT TRACKING FIELDS ===
  // Book and insight interaction tracking
  booksProgress: z.record(z.string(), UserBookProgressSchema), // Books user has interacted with
  insightsProgress: z.record(z.string(), UserInsightProgressSchema), // Insights/chapters user has engaged with
  
  // === USER UNDERSTANDING FIELDS ===
  // Complete onboarding responses for personalization
  onboardingAnswers: OnboardingAnswersCollectionSchema, // All answers from dynamic onboarding
  
  // AI-generated or manually created full profile description
  profileDescription: z.string().optional(), // DEPRECATED: Will be phased out in favor of profileSummary
  
  // Recent user-provided information for context
  activeContext: ActiveContextSchema, // Replaces the old recentUserContext
  coreProfile: CoreProfileSchema.optional(), // New long-term evolving profile
  
  // === FIELDS LIKELY NOT USED INITIALLY ===
  preAuthProfileId: z.string().optional(), // 🚫 May not be needed - depends on auth flow
  
  // === DEVICE INFO - STORED ONCE, NOT SENT WITH EVERY LOG ===
  deviceInfo: z.string().optional(), // Simple device string like "iOS v17.2"
  
  // === FLEXIBLE USER DATA ===
  userData: z.record(z.string(), z.string()), // Flexible key-value pairs for future use

  // === CALCULATION RESULTS (from pre-onboarding) ===
  calculationResults: CalculationResultsSchema.optional(), // Optional structured results from external onboarding calculations (e.g., parenting style scores)

  // === DEPRECATED FIELDS, NOT USED IN THIS APP ===
  books: z.record(z.string(), z.any()), // 🚫 DEPRECATED - use booksProgress instead
  chapters: z.record(z.string(), z.any()), // 🚫 DEPRECATED - use insightsProgress instead
  habits: z.record(z.string(), z.any()), // 🚫 DEPRECATED - not used in this app
  quotes: z.record(z.string(), z.any()), // 🚫 DEPRECATED - not used in this app
  futureBooksVoted: z.array(z.string()), // 🚫 DEPRECATED - may not be used

  chosenCategories: z.array(z.string()), // 🚫 DEPRECATED - use onboardingAnswers instead
  habitCategories: z.array(z.string()), // 🚫 DEPRECATED - not used in this app
  
  totalPoints: z.number(), // 🚫 DEPRECATED - may not implement gamification initially
  points: z.record(z.string(), z.any()), // 🚫 DEPRECATED - may not implement gamification initially

  invitationKey: z.string().optional(), // Invitation key - working profiles have this as string
  lastArticlesFetched: z.any().optional(), // 🚫 DEPRECATED - may not be used

  // === AI RECOMMENDATIONS & CHAT ===
  profileSummary: ProfileSummarySchema.optional(), // AI-generated profile summary
  currentRecommendations: DailyRecommendationsSchema.optional(), // Today's recommendations
  recommendationHistory: z.array(DailyRecommendationsSchema).optional(), // Past recommendations
  chatHistory: z.array(MentorChatHistorySchema).optional(), // Mentor chat history
});

// === TYPE EXPORTS ===

export type PrivateProfile = z.infer<typeof PrivateProfileSchema>;
export type UserBookProgress = z.infer<typeof UserBookProgressSchema>;
export type UserInsightProgress = z.infer<typeof UserInsightProgressSchema>;
export type OnboardingAnswersCollection = z.infer<typeof OnboardingAnswersCollectionSchema>;
export type OnboardingAnswer = z.infer<typeof OnboardingAnswerSchema>;
export type ActiveContext = z.infer<typeof ActiveContextSchema>;
export type CoreProfile = z.infer<typeof CoreProfileSchema>;
export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;
export type AIBookRecommendation = z.infer<typeof AIBookRecommendationSchema>;
export type AIInsightRecommendation = z.infer<typeof AIInsightRecommendationSchema>;
export type DailyRecommendations = z.infer<typeof DailyRecommendationsSchema>;
export type ProfileSummary = z.infer<typeof ProfileSummarySchema>;
export type MentorChatHistory = z.infer<typeof MentorChatHistorySchema>;

// === SUBSCRIPTION TYPES ===

export type SubscriptionTier = 'free' | 'standard_premium' | 'ai_premium';

// === UTILITY TYPES ===

export type PrivateProfileUpdate = Partial<PrivateProfile>;

export const PrivateProfileCreationDataSchema = z.object({
  id: z.string(),
  email: z.string().email().optional(),
  displayName: z.string().optional(),
  platform: z.string(),
  timezone: z.string().optional(),
  countryCode: z.string().nullable().optional(),
  onboardingAnswers: OnboardingAnswersCollectionSchema.optional(),
  profileDescription: z.string().optional(),
});

export type PrivateProfileCreationData = z.infer<typeof PrivateProfileCreationDataSchema>;

// === DEFAULT VALUES ===

export const createEmptyPrivateProfile = (
  userId: string,
  creationData: Partial<PrivateProfileCreationData> = {}
): PrivateProfile => {
  const now = Math.floor(Date.now() / 1000);
  
  const defaultProfile: any = {
    id: userId,
    createdAtTimestamp: now,
    updatedAtTimestamp: now,
    dailyActivity: [],
    platform: creationData.platform || 'React Native',
    
    // Subscription fields - default to free tier
    subscriptionTier: 'free' as const,
    subscriptionExpiresAt: null,
    lastRevenueCatSync: now,
    
    isTester: false,
    
    // New content tracking
    booksProgress: {},
    insightsProgress: {},
    
    // User understanding fields
    onboardingAnswers: creationData.onboardingAnswers || {
      sessionId: '',
      completedAt: 0,
      version: '1.0',
      answers: [],
    },
    activeContext: {
      lastUpdated: now,
      source: 'creation' as const,
      information: 'Profile created',
    },
    coreProfile: {
      lastUpdated: now,
      source: 'creation',
    },
    userData: {},
    
    // Deprecated fields - initialize but don't use
    books: {},
    chapters: {},
    habits: {},
    quotes: {},
    futureBooksVoted: [],
    chosenCategories: [],
    habitCategories: [],
    totalPoints: 0,
    points: {},

    // AI recommendations - initialize as empty arrays instead of undefined
    recommendationHistory: [],
  };

  // Only add optional fields if they have values
  if (creationData.email) {
    defaultProfile.email = creationData.email;
  }
  if (creationData.displayName) {
    defaultProfile.displayName = creationData.displayName;
  }
  if (creationData.timezone) {
    defaultProfile.timezone = creationData.timezone;
  }
  if (creationData.countryCode) {
    defaultProfile.countryCode = creationData.countryCode;
  }
  if (creationData.profileDescription) {
    defaultProfile.profileDescription = creationData.profileDescription;
  }

  // Validate and return
  return PrivateProfileSchema.parse(defaultProfile);
};

// === HELPER FUNCTIONS ===

export const updatePrivateProfileTimestamp = (
  profile: PrivateProfile
): PrivateProfile => {
  const updated = {
    ...profile,
    updatedAtTimestamp: Math.floor(Date.now() / 1000),
  };
  return PrivateProfileSchema.parse(updated);
};

export const addBookProgress = (
  profile: PrivateProfile,
  bookId: string
): PrivateProfile => {
  const now = Math.floor(Date.now() / 1000);
  
  const newBookProgress = UserBookProgressSchema.parse({
    id: bookId,
    startedAt: now,
    lastAccessedAt: now,
    completionPercentage: 0,
    isFavorite: false,
    totalTimeSpent: 0,
    notesCount: 0,
    isCompleted: false,
  });

  const updated = {
    ...profile,
    booksProgress: {
      ...profile.booksProgress,
      [bookId]: newBookProgress,
    },
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const updateBookProgress = (
  profile: PrivateProfile,
  bookId: string,
  updates: Partial<UserBookProgress>
): PrivateProfile => {
  const currentProgress = profile.booksProgress[bookId];
  if (!currentProgress) return profile;
  
  const now = Math.floor(Date.now() / 1000);
  
  const updatedProgress = UserBookProgressSchema.parse({
    ...currentProgress,
    ...updates,
    lastAccessedAt: now,
  });

  const updated = {
    ...profile,
    booksProgress: {
      ...profile.booksProgress,
      [bookId]: updatedProgress,
    },
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const updateActiveContext = (
  profile: PrivateProfile,
  context: Partial<Omit<ActiveContext, 'lastUpdated' | 'source'>>,
  source: ActiveContext['source']
): PrivateProfile => {
  const now = Math.floor(Date.now() / 1000);

  const updated = {
    ...profile,
    activeContext: {
      ...profile.activeContext,
      ...context,
      lastUpdated: now,
      source,
    },
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const updateOnboardingAnswers = (
  profile: PrivateProfile,
  onboardingData: OnboardingAnswersCollection
): PrivateProfile => {
  const now = Math.floor(Date.now() / 1000);
  
  const updated = {
    ...profile,
    onboardingAnswers: onboardingData,
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

// === NEW AI RECOMMENDATION FUNCTIONS ===

export const updateProfileSummary = (
  profile: PrivateProfile,
  summary: ProfileSummary
): PrivateProfile => {
  const now = Date.now() / 1000; // Keep as float, don't use Math.floor
  
  const updated = {
    ...profile,
    profileSummary: {
      ...summary,
      lastUpdated: now,
    },
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const updateDailyRecommendations = (
  profile: PrivateProfile,
  recommendations: DailyRecommendations
): PrivateProfile => {
  const now = Date.now() / 1000; // Keep as float
  
  // Add to history if we have previous recommendations
  const history = profile.recommendationHistory || [];
  if (profile.currentRecommendations) {
    history.push(profile.currentRecommendations);
  }
  
  const updated = {
    ...profile,
    currentRecommendations: {
      ...recommendations,
      // Keep the original date from recommendations, don't overwrite it
      generatedAt: recommendations.generatedAt || now,
    },
    recommendationHistory: history.slice(-10), // Keep last 10 days
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const addChatHistory = (
  profile: PrivateProfile,
  chatEntry: MentorChatHistory
): PrivateProfile => {
  const now = Date.now() / 1000; // Keep as float
  const history = profile.chatHistory || [];

  const updated = {
    ...profile,
    chatHistory: [...history, chatEntry].slice(-50), // Keep last 50 chats
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
};

export const updateCoreProfile = (
  profile: PrivateProfile,
  updates: Partial<Omit<CoreProfile, 'lastUpdated' | 'source'>>,
  source: string,
): PrivateProfile => {
  const now = Math.floor(Date.now() / 1000);

  const updated = {
    ...profile,
    coreProfile: {
      ...profile.coreProfile,
      ...updates,
      lastUpdated: now,
      source,
    },
    updatedAtTimestamp: now,
  };

  return PrivateProfileSchema.parse(updated);
}; 